import { Fragment, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ErrorBanner, ListState, formatUtc, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { awaitingReview } from './awaitingReview'
import { PageTitle } from '../../components/PageTitle'
import { POLICIES_COPY, emptyStateFor } from '../../lib/emptyState'
import { describePolicy } from './capabilityOutcome'
import { ApiError, api, policies as policyApi, trust, type PolicySummary } from '../../lib/api'
import { paramFrom, withParams } from '../../lib/tabs'
import { groupCountLabel, segmentPolicies } from './policyGroups'
import { nextOffset, pageState, previousOffset } from '../explore/paging'

/**
 * The policy list, and the only place a policy can be taken out of service.
 *
 * Retiring is here rather than on the editor because the problem it solves is a list one. A tenant
 * accumulates policies from templates, tests and abandoned drafts, and removing them one screen at a
 * time is not a thing anybody does — the dictionary list learned this at 210 rows (D-204), and the
 * policy list reached 244 before it had any removal path at all.
 *
 * "Retire" and "stop running" are two different acts and the form says so, because the agent keeps
 * running a policy whose publication is live no matter what the console calls it. Retiring alone
 * tidies this screen and changes nothing on any machine, which is a real thing to want and a terrible
 * thing to do by accident, so the second half is a checkbox that is on by default and named plainly.
 *
 * That checkbox is also the most dangerous control in this console, and the reason the confirmation
 * is a modal rather than the panel it used to be. Select every row, leave the box ticked, and this
 * screen revokes every publication the organization has: a signed tombstone reaches each machine at
 * its next check-in and the fleet stops. Nothing here undoes that — republishing is a new publication
 * that has to travel the same way - so with the box ticked the phrase has to be typed, which is the
 * one thing a pointer already moving toward the button cannot do by accident.
 */

/** Enough concurrency to clear a few hundred rows quickly, few enough not to flood the API. */
const LANES = 6

/**
 * Typed before a retirement is allowed to reach the fleet.
 *
 * Words rather than the policy count, because the count changes with the selection and a phrase that
 * changes is a phrase somebody copies out of the label without reading. This one says what pressing
 * the button does.
 */
const STOP_PHRASE = 'stop the fleet'

/** Under the server's ceiling of 500, so the server returns exactly this many rows. */
const PAGE = 200
const POLICIES = { one: 'policy', many: 'policies' }

const STATUSES: [string, string][] = [
  ['active', 'Active'],
  ['retired', 'Retired'],
  ['all', 'Active and retired']
]

export function PolicyListPage() {
  // The search and the status filter are in the URL, like every other list in this console (D-441),
  // so a link to "retired policies matching Claude" is that list.
  const location = useLocation()
  const navigate = useNavigate()
  const search = paramFrom(location.search, 'search') ?? ''
  const requestedStatus = paramFrom(location.search, 'status')
  const status = STATUSES.some(([value]) => value === requestedStatus) ? requestedStatus! : 'active'
  const offset = Math.max(0, Number.parseInt(paramFrom(location.search, 'offset') ?? '0', 10) || 0)
  // Any change to the query starts at the first page, or page four of the old result is page four of
  // the new one.
  const setSearch = (value: string) =>
    navigate({ search: withParams(location.search, { search: value || null, offset: null }) }, { replace: true })
  const setStatus = (value: string) =>
    navigate(
      { search: withParams(location.search, { status: value === 'active' ? null : value, offset: null }) },
      { replace: true }
    )
  const setOffset = (value: number) => {
    setSelected(new Set())
    navigate({ search: withParams(location.search, { offset: value > 0 ? String(value) : null }) }, { replace: true })
  }
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')
  const [alsoRevoke, setAlsoRevoke] = useState(true)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  // Two hundred so a tenant that has accumulated a few hundred policies can be cleared a page at a
  // time. This list used to ask for two hundred and say nothing about the rest, so a policy that
  // sorted past the two hundredth was saved and then nowhere to be found (the D-430 class).
  const list = useAsync(() => api.listPolicies(search || undefined, status, PAGE, offset), [search, status, offset])

  const rows = list.value?.items ?? []
  const paging = list.value ? pageState(list.value.total, list.value.offset, rows.length, POLICIES) : null
  const selectable = rows.filter((policy) => policy.status !== 'retired')
  const allSelected = selectable.length > 0 && selectable.every((policy) => selected.has(policy.policyId))

  const toggle = (policyId: string, on: boolean) => {
    const next = new Set(selected)
    if (on) next.add(policyId)
    else next.delete(policyId)
    setSelected(next)
  }

  const toggleAll = (on: boolean) =>
    setSelected(on ? new Set(selectable.map((policy) => policy.policyId)) : new Set())

  const retire = async () => {
    setBusy(true)
    setFailure(null)
    setNotice(null)

    const targets = rows.filter((policy) => selected.has(policy.policyId))
    let retired = 0
    let stopped = 0
    const problems: string[] = []

    const worker = async (queue: typeof targets) => {
      for (const policy of queue) {
        try {
          // Revoking first. A policy retired but still published is still running, and if the second
          // call is the one that fails that is the state you are left in — so the order puts the
          // failure where it stops the fleet rather than where it only tidies a list.
          if (alsoRevoke) {
            try {
              await trust.revoke(policy.policyId, reason)
              stopped++
            } catch (error) {
              // Never published, or already revoked. Not a reason to leave the policy active.
              if (!(error instanceof ApiError) || error.status !== 400) throw error
            }
          }

          await policyApi.retire(policy.policyId, reason)
          retired++
        } catch (error) {
          problems.push(`${policy.name}: ${error instanceof ApiError ? error.message : 'failed'}`)
        }
      }
    }

    const lanes = Array.from({ length: Math.min(LANES, targets.length) }, (_, lane) =>
      worker(targets.filter((_, index) => index % LANES === lane))
    )

    try {
      await Promise.all(lanes)
      setNotice(
        `Retired ${retired} ${retired === 1 ? 'policy' : 'policies'}` +
          (alsoRevoke ? `, and stopped ${stopped} of them running on the fleet.` : '. They are still running on any device that has them.') +
          (problems.length > 0 ? ` ${problems.length} could not be retired.` : '')
      )
      if (problems.length > 0) setFailure(problems.slice(0, 5).join('; '))
      setSelected(new Set())
      setConfirming(false)
      setReason('')
      list.reload()
    } finally {
      setBusy(false)
    }
  }

  const review = awaitingReview(list.value?.items ?? null)

  // An approved revision is the one that gets published, so a selection containing none of them
  // cannot stop anything however the checkbox is left. Counted from rows already on screen rather
  // than asked of the server per policy, which would be one request each for a selection of 200.
  const published = rows.filter(
    (policy) => selected.has(policy.policyId) && policy.approvedRevision !== null
  ).length

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>Policies</PageTitle>
          <p>What the fleet runs, and which revision each device has been given.</p>
        </div>
        <Link className="button primary" to="/policies/build" data-testid="new-policy">
          New policy
        </Link>
      </div>

      <ErrorBanner message={failure ?? list.error} />

      {/*
        What is waiting for a person, and the failure mode it exists to catch.

        A draft reaches no device and changes nothing, so a queue of unreviewed drafts is a queue of
        work somebody believes is done. That was survivable while every draft was typed by the person
        looking at this screen; it stopped being survivable when an analyst could produce one from a
        case and then wait. Derived from the rows already listed, and silent when the list is a read
        that failed rather than a fleet with nothing outstanding (D-263).
      */}
      {review.message && (
        <div className="banner" role="status" data-testid="awaiting-review">
          {review.message}
        </div>
      )}

      {notice && (
        <div className="banner success" role="status" data-testid="policy-list-notice">
          {notice}
        </div>
      )}

      <div className="card">
        <div className="card-body toolbar">
          <input
            className="grow"
            type="search"
            placeholder="Search policies"
            aria-label="Search policies"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value)
              setSelected(new Set())
            }}
            aria-label="Status"
            data-testid="policy-status"
          >
            {STATUSES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <button type="button" onClick={list.reload} disabled={busy}>
            Refresh
          </button>
          <button
            type="button"
            className="danger"
            onClick={() => setConfirming(true)}
            disabled={busy || selected.size === 0}
            data-testid="retire-selected"
          >
            Retire {selected.size > 0 ? selected.size : ''}
          </button>
        </div>

        <div className="table-scroll">
          <table data-testid="policy-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(event) => toggleAll(event.target.checked)}
                    aria-label="Select every policy shown"
                    disabled={selectable.length === 0}
                    data-testid="select-all-policies"
                  />
                </th>
                <th>Name</th>
                <th>What it does</th>
                <th className="secondary">Latest</th>
                <th>Approved</th>
                <th className="secondary">Published hash</th>
                <th className="secondary">Created</th>
              </tr>
            </thead>
            <tbody>
              {segmentPolicies(rows).map((segment) =>
                segment.kind === 'policy' ? (
                  <PolicyRow key={segment.policy.policyId} policy={segment.policy} selected={selected} onToggle={toggle} />
                ) : (
                  <Fragment key={`group-${segment.groupId}-${segment.policies[0]!.policyId}`}>
                    <GroupRow
                      groupId={segment.groupId}
                      name={segment.name}
                      label={groupCountLabel(segment.policies.length, segment.size)}
                      members={segment.policies}
                      selected={selected}
                      onToggle={(on) => {
                        const next = new Set(selected)
                        for (const member of segment.policies.filter((policy) => policy.status !== 'retired'))
                          if (on) next.add(member.policyId)
                          else next.delete(member.policyId)
                        setSelected(next)
                      }}
                    />
                    {segment.policies.map((policy) => (
                      <PolicyRow key={policy.policyId} policy={policy} selected={selected} onToggle={toggle} member />
                    ))}
                  </Fragment>
                )
              )}
            </tbody>
          </table>
        </div>

        <ListState
          kind={emptyStateFor({
            value: list.value ? rows : null,
            error: failure ?? list.error,
            loading: list.loading,
            filtered: search !== '' || status !== 'active'
          })}
          copy={POLICIES_COPY}
          columns={7}
          onAction={() => {
            if (list.error) list.reload()
            else {
              setSearch('')
              setStatus('active')
            }
          }}
        />

        {paging && rows.length > 0 && (
          <div className="card-body toolbar">
            <span className="hint grow" data-testid="policy-count">
              {paging.summary}
            </span>
            <button
              type="button"
              onClick={() => setOffset(previousOffset(offset, PAGE))}
              disabled={!paging.canGoBack || busy}
              data-testid="policy-previous"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setOffset(nextOffset(list.value!.total, offset, PAGE))}
              disabled={!paging.canGoForward || busy}
              data-testid="policy-next"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        title={`Retire ${selected.size} ${selected.size === 1 ? 'policy' : 'policies'}`}
        confirmLabel={alsoRevoke ? 'Retire and stop them running' : 'Retire, leave them running'}
        danger={alsoRevoke}
        busy={busy}
        confirmDisabled={!reason.trim()}
        // Only when the fleet is going to be told. Retiring alone changes nothing on any machine and
        // can be undone by publishing again; revoking has already stopped the device by the time
        // anybody regrets it.
        confirmPhrase={alsoRevoke ? STOP_PHRASE : undefined}
        onConfirm={retire}
        onClose={() => setConfirming(false)}
        data-testid="retire-confirm"
      >
        {alsoRevoke && published > 0 && (
          <div className="banner warning" role="note" data-testid="retire-fleet-warning">
            {published === 1
              ? 'One of these has an approved revision, so it may be published and running.'
              : `${published} of these have an approved revision, so they may be published and running.`}{' '}
            Revoking serves a signed tombstone: every device holding one stops running it at its next
            check-in, and republishing later is a new rollout rather than an undo.
          </div>
        )}

        <p>
          Their revisions and the events they produced are kept — those are the record of what the
          fleet was told to do, and deleting them would answer no question anybody asks later.
        </p>

        <div className="field">
          <label htmlFor="retire-reason">Why</label>
          <input
            id="retire-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Accumulated test policies, no longer needed."
            data-testid="retire-reason"
          />
          <div className="hint">Recorded against each policy, so the audit trail says why.</div>
        </div>

        <label className="inline-check">
          <input
            type="checkbox"
            checked={alsoRevoke}
            onChange={(event) => setAlsoRevoke(event.target.checked)}
            data-testid="retire-also-revoke"
          />
          <span>Also stop devices running them</span>
        </label>
        <div className="hint">
          Retiring only stops a policy being edited or published. A device keeps running whatever was
          published to it until the publication is revoked, which serves a signed tombstone telling it
          to stop. Leave this on unless you mean to tidy the list and change nothing.
        </div>
      </ConfirmDialog>
    </>
  )
}

/**
 * The heading over a builder group's policies: its name, how many it holds, and a link to the page
 * where they are approved and published together. The count is the server's; when the page shows
 * only some of the group it says so rather than reading as a smaller group.
 */
function GroupRow({
  groupId,
  name,
  label,
  members,
  selected,
  onToggle
}: {
  groupId: string
  name: string
  label: string
  members: PolicySummary[]
  selected: ReadonlySet<string>
  onToggle: (on: boolean) => void
}) {
  const selectable = members.filter((policy) => policy.status !== 'retired')
  const all = selectable.length > 0 && selectable.every((policy) => selected.has(policy.policyId))

  return (
    <tr className="group-row" data-testid={`policy-group-${groupId}`}>
      <td>
        {selectable.length > 0 && (
          <input
            type="checkbox"
            checked={all}
            onChange={(event) => onToggle(event.target.checked)}
            aria-label={`Select every policy in ${name}`}
          />
        )}
      </td>
      <td colSpan={6}>
        <Link to={`/policies/groups/${groupId}`} className="group-name" data-testid="policy-group-link">
          {name}
        </Link>{' '}
        <span className="muted" data-testid="policy-group-count">
          · {label}, built together
        </span>
      </td>
    </tr>
  )
}

function PolicyRow({
  policy,
  selected,
  onToggle,
  member = false
}: {
  policy: PolicySummary
  selected: ReadonlySet<string>
  onToggle: (policyId: string, on: boolean) => void
  member?: boolean
}) {
  return (
    <tr className={member ? 'group-member' : undefined}>
      <td>
        {policy.status === 'retired' ? (
          <span className="muted" title="Already retired">
            —
          </span>
        ) : (
          <input
            type="checkbox"
            checked={selected.has(policy.policyId)}
            onChange={(event) => onToggle(policy.policyId, event.target.checked)}
            aria-label={`Select ${policy.name}`}
          />
        )}
      </td>
      <td>
        <Link to={`/policies/${policy.policyId}`}>{policy.name}</Link>
        {policy.status === 'retired' && <span className="pill"> retired</span>}
        {policy.description && <div className="muted">{policy.description}</div>}
      </td>
      {/*
        What this policy actually does, in a sentence. It used to be the capability identifier, which
        is why a thousand rows were unreadable - every one said `agent-prompt-observe` and they were
        all the same picture.
      */}
      <td className="muted">{describePolicy(policy)}</td>
      <td className="secondary">{policy.latestRevision}</td>
      <td>
        {policy.approvedRevision === null ? (
          <span className="muted">Draft only</span>
        ) : (
          <span className="pill sev-2">Revision {policy.approvedRevision}</span>
        )}
      </td>
      {/* Eight characters is enough to compare two deployments by eye and short enough not to wreck
          the column width. */}
      <td className="mono secondary">{policy.approvedHash ? policy.approvedHash.slice(0, 8) : '—'}</td>
      <td className="muted secondary">{formatUtc(policy.createdUtc)}</td>
    </tr>
  )
}
